class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
// === Smooth remove of last item + auto drawer refresh (no re-adding bug) ===
(function () {
  const DRAWER_SECTION = 'cart-drawer';
  const DRAWER_SELECTOR = '.cart-drawer';

  // Helper: fade out animation
  async function fadeOut(el) {
    el.style.transition = 'opacity .25s ease, transform .25s ease';
    el.style.opacity = '0';
    el.style.transform = 'translateY(10px)';
    await new Promise(r => setTimeout(r, 250));
  }

  // Helper: reload drawer HTML cleanly
  async function refreshDrawer() {
    try {
      const res = await fetch(`/?section_id=${DRAWER_SECTION}`);
      if (!res.ok) return;
      const html = await res.text();
      const newDrawer = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(DRAWER_SELECTOR);
      const oldDrawer = document.querySelector(DRAWER_SELECTOR);
      if (newDrawer && oldDrawer) oldDrawer.replaceWith(newDrawer);

      // auto-close if empty
      const isEmpty = !newDrawer.querySelector('[data-cart-item]');
      if (isEmpty) document.querySelector('cart-drawer')?.close?.();
    } catch (err) {
      console.error('Drawer refresh failed:', err);
    }
  }

  // Helper: get line index for Shopify API
  function getLineIndex(cartItem) {
    const line =
      cartItem?.dataset?.line ||
      cartItem?.getAttribute('data-line') ||
      cartItem.querySelector('input[name="updates[]"]')?.dataset?.line;
    return line ? Number(line) : null;
  }

  // === Listen for minus button clicks ===
  document.addEventListener('click', async (e) => {
    const minusBtn = e.target.closest('[data-cart-quantity-decrease], .quantity__button--decrease]');
    if (!minusBtn) return;

    // Prevent Shopify’s native handler from running
    e.preventDefault();
    e.stopImmediatePropagation();

    const cartItem = e.target.closest('[data-cart-item], .cart-item, .cart-drawer__item]');
    if (!cartItem) return;

    const input = cartItem.querySelector('input[name="quantity"], input[name="updates[]"]');
    const currentQty = parseInt(input?.value || '1', 10);

    // Only handle when trying to go from 1 -> 0
    if (currentQty > 1) {
      // Let Shopify handle regular decrease
      minusBtn.removeAttribute('data-custom-remove');
      input.value = currentQty - 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Mark as custom remove to skip re-add from global.js
    minusBtn.dataset.customRemove = 'true';

    await fadeOut(cartItem);

    const line = getLineIndex(cartItem);
    if (!line) return;

    try {
      // Remove item via Shopify API
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity: 0 }),
      });

      // Re-render drawer
      await refreshDrawer();
    } catch (err) {
      console.error('Error removing item:', err);
      cartItem.style.opacity = '1';
      cartItem.style.transform = 'none';
    }
  });
})();
// === DIAGNOSTIC: Monitor cart-drawer DOM mutations and log stack traces for additions ===
(function () {
  const drawer = document.querySelector('.cart-drawer');
  if (!drawer) return;
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.querySelector && node.querySelector('[data-cart-item]') || node.hasAttribute && node.hasAttribute('data-cart-item')) {
            console.warn('Cart item node added back:', node);
            console.trace('Trace: who added this node?');
          }
        });
      }
    }
  });
  obs.observe(drawer, { childList: true, subtree: true });
  // Stop observing after 30s (temporary)
  setTimeout(() => obs.disconnect(), 30000);
})();
// === Smooth auto-remove when quantity reaches 0 (Final Unified Version) ===
(function () {
  const DRAWER_SELECTOR = '.cart-drawer';
  const DRAWER_SECTION_URL = '/?section_id=cart-drawer';

  // Helper: get line index safely
  function getLineIndex(el) {
    const line = el?.dataset.line || el?.getAttribute('data-line');
    if (line) return Number(line);
    const input = el.querySelector('input[name="updates[]"], input[name="quantity"]');
    const inputLine = input?.dataset.line || input?.getAttribute('data-line');
    return inputLine ? Number(inputLine) : null;
  }

  // Helper: update count UI
  function updateCartCountDisplays(cart) {
    const count = cart.item_count ?? (cart.items?.length ?? 0);
    document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count')
      .forEach(el => el.textContent = count);
  }

  // Helper: refresh drawer HTML
  async function refreshCartDrawer() {
    const res = await fetch(DRAWER_SECTION_URL);
    if (!res.ok) return;
    const html = await res.text();
    const newDrawer = new DOMParser().parseFromString(html, 'text/html').querySelector(DRAWER_SELECTOR);
    const oldDrawer = document.querySelector(DRAWER_SELECTOR);
    if (newDrawer && oldDrawer) oldDrawer.replaceWith(newDrawer);
  }

  document.addEventListener('click', async (e) => {
    const minusBtn = e.target.closest('[data-cart-quantity-decrease], .quantity__button--decrease, .cart__qty-decrease');
    if (!minusBtn) return;

    // prevent double-firing
    if (minusBtn.dataset.removing === 'true') return;
    minusBtn.dataset.removing = 'true';

    const cartItem = minusBtn.closest('[data-cart-item], .cart-item, .cart-drawer__item');
    if (!cartItem) return;

    const input = cartItem.querySelector('input[name="quantity"], input[name="updates[]"]');
    const currentQty = parseInt(input?.value || '1', 10);
    const newQty = currentQty - 1;

    // If still above zero → let theme handle it
    if (newQty > 0) {
      minusBtn.dataset.removing = 'false';
      return;
    }

    // Prevent Shopify’s built-in validation
    e.preventDefault();
    e.stopImmediatePropagation();

    // Fade out animation
    cartItem.classList.add('removing');
    await new Promise(r => setTimeout(r, 250));

    const line = getLineIndex(cartItem);
    if (!line) return;

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity: 0 }),
      });

      if (!res.ok) throw new Error('Failed to remove item');
      const cart = await res.json();

      // Remove from DOM instantly
      if (cartItem && cartItem.parentNode) cartItem.remove();

      // Update cart counts and re-render drawer
      updateCartCountDisplays(cart);
      await refreshCartDrawer();

      // Optional: play sound
      const sound = document.getElementById('cart-remove-sound');
      if (sound) sound.play().catch(() => {});
    } catch (err) {
      console.error('Cart item remove failed:', err);
      cartItem.classList.remove('removing');
    } finally {
      minusBtn.dataset.removing = 'false';
    }
  });
})();
